import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';

interface FormExpirationGuardProps {
  /** ISO date string or Date object — form expires at the END of this date (23:59:59) unless expirationTime is set */
  expirationDate: string | Date;
  /** Optional time in HH:mm or HH:mm:ss format — if set, form expires at this exact time instead of 23:59:59 */
  expirationTime?: string;
  /** IANA timezone for the expiration check (default: 'Europe/Berlin') */
  timezone?: string;
  /** Content to show when the form is still active */
  children: ReactNode;
  /** Optional custom message for the expired state */
  expiredMessage?: string;
  /** Optional custom title for the expired state */
  expiredTitle?: string;
}

/**
 * Wraps a form and shows an expiration notice once the configured date has passed.
 * Reusable across any form page.
 */
export const FormExpirationGuard = ({
  expirationDate,
  children,
  expirationTime,
  timezone = 'Europe/Berlin',
  expiredTitle = 'Aktion abgelaufen',
  expiredMessage = 'Der Anmeldezeitraum für diese Aktion ist leider abgelaufen. Eine Anmeldung ist nicht mehr möglich.',
}: FormExpirationGuardProps) => {
  // Get current time components projected into the target timezone using
  // Intl.DateTimeFormat.formatToParts so both "now" and "deadline" are
  // expressed in the same timezone coordinate system — regardless of the
  // user's local timezone.
  const nowFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const nowParts = nowFormatter.formatToParts(new Date());
  const getPart = (type: string) =>
    Number(nowParts.find((p) => p.type === type)?.value ?? '0');
  const nowValue =
    getPart('year') * 10000000000 +
    getPart('month') * 100000000 +
    getPart('day') * 1000000 +
    getPart('hour') * 10000 +
    getPart('minute') * 100 +
    getPart('second');

  // Parse the date portion of the expiration deadline
  const dateStr =
    expirationDate instanceof Date
      ? `${expirationDate.getFullYear()}-${String(expirationDate.getMonth() + 1).padStart(2, '0')}-${String(expirationDate.getDate()).padStart(2, '0')}`
      : String(expirationDate).split('T')[0];
  const [deadY, deadM, deadD] = dateStr.split('-').map(Number);

  // Parse expiration time; default seconds to 0 when only HH:mm is provided.
  // Fall back to 23:59:59 (end-of-day) when no time is specified at all.
  const timeParts = expirationTime
    ? expirationTime.split(':').map(Number)
    : [23, 59, 59];
  const deadH = timeParts[0];
  const deadMin = timeParts[1] ?? 0;
  const deadS = timeParts[2] ?? 0;

  // Build a comparable numeric value for the deadline in the same timezone
  const deadlineValue =
    deadY * 10000000000 +
    deadM * 100000000 +
    deadD * 1000000 +
    deadH * 10000 +
    deadMin * 100 +
    deadS;

  const isExpired = nowValue > deadlineValue;

  if (!isExpired) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
        {expiredTitle}
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        {expiredMessage}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Bei Fragen wende Dich gerne an{' '}
        <a href="mailto:marketing@lansco.de">marketing@lansco.de</a>.
      </Typography>
    </Box>
  );
};
